<template>
  <div class="p-6 max-w-7xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">IAM Configuration</h1>
        <p class="text-gray-600 dark:text-gray-400 mt-2">
          View current IAM scopes, permissions, and role mappings
        </p>
        <div class="mt-2">
          <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
            <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            Staging Environment
          </span>
        </div>
      </div>
      
      <!-- Refresh Button -->
      <button
        @click="refreshConfig"
        :disabled="loading"
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg
          v-if="!loading"
          class="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <svg
          v-else
          class="w-4 h-4 mr-2 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {{ loading ? 'Refreshing...' : 'Refresh Config' }}
      </button>
    </div>

    <!-- Version Info -->
    <div v-if="iamConfig" class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div class="flex items-center">
        <svg class="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
        </svg>
        <span class="text-blue-800 dark:text-blue-200 font-medium">
          Configuration Version: {{ iamConfig.version }}
        </span>
        <span class="ml-4 text-blue-600 dark:text-blue-300 text-sm">
          Last updated: {{ lastUpdated }}
        </span>
        <span class="ml-4 text-blue-600 dark:text-blue-300 text-sm">
          Environment: Staging
        </span>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="mb-6">
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search scopes, roles, or permissions..."
          class="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
        />
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <div class="flex items-center">
        <svg class="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <span class="text-red-800 dark:text-red-200">{{ error }}</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !iamConfig" class="flex items-center justify-center py-12">
      <div class="text-center">
        <svg class="mx-auto h-12 w-12 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p class="mt-2 text-gray-600 dark:text-gray-400">Loading IAM configuration...</p>
      </div>
    </div>

    <!-- Content -->
    <div v-if="iamConfig && !loading" class="space-y-6">
      <!-- Scopes Section -->
      <ExpandableSection
        title="Defined Scopes"
        :count="filteredScopes.length"
        :is-expanded="expandedSections.scopes"
        @toggle="expandedSections.scopes = !expandedSections.scopes"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div
            v-for="scope in filteredScopes"
            :key="scope"
            class="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <span class="font-mono text-sm text-gray-900 dark:text-white">{{ scope }}</span>
          </div>
        </div>
      </ExpandableSection>

      <!-- Scope Configs Section -->
      <ExpandableSection
        title="Scope Configurations"
        :count="filteredScopeConfigs.length"
        :is-expanded="expandedSections.scopeConfigs"
        @toggle="expandedSections.scopeConfigs = !expandedSections.scopeConfigs"
      >
        <div class="space-y-4">
          <div
            v-for="[scopeName, config] in filteredScopeConfigs"
            :key="scopeName"
            class="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <h4 class="font-semibold text-gray-900 dark:text-white mb-3">{{ scopeName }}</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h5 class="font-medium text-gray-700 dark:text-gray-300 mb-2">Auth Classes</h5>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="authClass in config.whoCanAccessAuthClasses"
                    :key="authClass"
                    class="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded"
                  >
                    {{ authClass }}
                  </span>
                </div>
              </div>
              <div v-if="config.whoCanAccessIstioMeshApis && config.whoCanAccessIstioMeshApis.length > 0">
                <h5 class="font-medium text-gray-700 dark:text-gray-300 mb-2">Istio Mesh APIs</h5>
                <div class="space-y-2">
                  <div class="flex items-center p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <svg class="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-xs text-yellow-800 dark:text-yellow-200 font-medium">
                      This check is not yet implemented in IAM
                    </span>
                  </div>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="api in config.whoCanAccessIstioMeshApis"
                      :key="api"
                      class="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs rounded"
                    >
                      {{ api }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ExpandableSection>

      <!-- User Role Scopes Section -->
      <ExpandableSection
        title="User Role Scopes"
        :count="filteredUserRoleScopes.length"
        :is-expanded="expandedSections.userRoleScopes"
        @toggle="expandedSections.userRoleScopes = !expandedSections.userRoleScopes"
      >
        <div class="space-y-4">
          <div
            v-for="[role, scopes] in filteredUserRoleScopes"
            :key="role"
            class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              @click="toggleRoleSection(role)"
              class="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
              <div class="flex items-center justify-between">
                <h4 class="font-semibold text-gray-900 dark:text-white">{{ role }}</h4>
                <div class="flex items-center">
                  <span class="mr-3 text-sm text-gray-500 dark:text-gray-400">
                    {{ scopes.length }} scope{{ scopes.length !== 1 ? 's' : '' }}
                  </span>
                  <svg
                    class="w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform duration-200"
                    :class="{ 'rotate-180': expandedRoleSections[role] }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </button>
            <div
              v-show="expandedRoleSections[role]"
              class="px-4 pb-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div class="flex flex-wrap gap-2 pt-3">
                <span
                  v-for="scope in scopes"
                  :key="scope"
                  class="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-sm rounded"
                >
                  {{ scope }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </ExpandableSection>

      <!-- User Role Assigned Scopes Section -->
      <ExpandableSection
        title="User Role Assigned Scopes"
        :count="filteredUserRoleAssignedScopes.length"
        :is-expanded="expandedSections.userRoleAssignedScopes"
        @toggle="expandedSections.userRoleAssignedScopes = !expandedSections.userRoleAssignedScopes"
      >
        <div class="space-y-4">
          <div
            v-for="[role, scopes] in filteredUserRoleAssignedScopes"
            :key="role"
            class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              @click="toggleAssignedRoleSection(role)"
              class="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
              <div class="flex items-center justify-between">
                <h4 class="font-semibold text-gray-900 dark:text-white">{{ role }}</h4>
                <div class="flex items-center">
                  <span class="mr-3 text-sm text-gray-500 dark:text-gray-400">
                    {{ scopes.length }} scope{{ scopes.length !== 1 ? 's' : '' }}
                  </span>
                  <svg
                    class="w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform duration-200"
                    :class="{ 'rotate-180': expandedAssignedRoleSections[role] }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </button>
            <div
              v-show="expandedAssignedRoleSections[role]"
              class="px-4 pb-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div class="flex flex-wrap gap-2 pt-3">
                <span
                  v-for="scope in scopes"
                  :key="scope"
                  class="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-sm rounded"
                >
                  {{ scope }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </ExpandableSection>

      <!-- URL to Scope Mapping Section -->
      <ExpandableSection
        title="Endpoint Scope Mapping"
        :count="filteredUrlScopeMap.length"
        :is-expanded="expandedSections.urlScopeMap"
        @toggle="expandedSections.urlScopeMap = !expandedSections.urlScopeMap"
      >
        <div class="space-y-4">
          <div
            v-for="[url, scope] in filteredUrlScopeMap"
            :key="url"
            class="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h5 class="font-medium text-gray-700 dark:text-gray-300 mb-2">Endpoint</h5>
                <div class="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded border">
                  {{ url }}
                </div>
              </div>
              <div>
                <h5 class="font-medium text-gray-700 dark:text-gray-300 mb-2">Maps to Scope</h5>
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                  {{ scope }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </ExpandableSection>
    </div>

    <!-- Initial Load Button -->
    <div v-if="!iamConfig && !loading && !error" class="text-center py-12">
      <button
        @click="loadConfig"
        class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Load IAM Configuration
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { IIAMConfig } from '../../types/iam-config'
import { iamConfigService } from '../../services/iam/iamConfigService'
import ExpandableSection from './components/ExpandableSection.vue'

// State
const iamConfig = ref<IIAMConfig | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const searchQuery = ref('')
const expandedSections = ref({
  scopes: true,
  scopeConfigs: true,
  userRoleScopes: true,
  userRoleAssignedScopes: true,
  urlScopeMap: true
})

// State for individual role section expansion
const expandedRoleSections = ref<Record<string, boolean>>({})
const expandedAssignedRoleSections = ref<Record<string, boolean>>({})

// Computed properties for filtering
const filteredScopes = computed(() => {
  if (!iamConfig.value) return []
  if (!searchQuery.value) return iamConfig.value.config.scopes
  
  return iamConfig.value.config.scopes.filter(scope =>
    scope.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const filteredScopeConfigs = computed(() => {
  if (!iamConfig.value) return []
  if (!searchQuery.value) return Object.entries(iamConfig.value.config.scopeConfigs)
  
  return Object.entries(iamConfig.value.config.scopeConfigs).filter(([scopeName, config]) =>
    scopeName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    config.whoCanAccessAuthClasses.some(authClass => 
      authClass.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  )
})

const filteredUserRoleScopes = computed(() => {
  if (!iamConfig.value) return []
  if (!searchQuery.value) return Object.entries(iamConfig.value.config.userRoleScopesMap)
  
  return Object.entries(iamConfig.value.config.userRoleScopesMap).filter(([role, scopes]) =>
    role.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    scopes.some(scope => scope.toLowerCase().includes(searchQuery.value.toLowerCase()))
  )
})

const filteredUserRoleAssignedScopes = computed(() => {
  if (!iamConfig.value) return []
  if (!searchQuery.value) return Object.entries(iamConfig.value.config.userRoleAssignedScopesMap)
  
  return Object.entries(iamConfig.value.config.userRoleAssignedScopesMap).filter(([role, scopes]) =>
    role.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    scopes.some(scope => scope.toLowerCase().includes(searchQuery.value.toLowerCase()))
  )
})

const filteredUrlScopeMap = computed(() => {
  if (!iamConfig.value) return []
  if (!searchQuery.value) return Object.entries(iamConfig.value.config.urlScopeMap)
  
  return Object.entries(iamConfig.value.config.urlScopeMap).filter(([url, scope]) =>
    url.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    scope.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const lastUpdated = computed(() => {
  if (!iamConfig.value) return ''
  return new Date().toLocaleString()
})

// Methods
const loadConfig = async () => {
  try {
    loading.value = true
    error.value = null
    iamConfig.value = await iamConfigService.fetchIAMConfig()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'An unexpected error occurred'
  } finally {
    loading.value = false
  }
}

const refreshConfig = async () => {
  await loadConfig()
}

// Toggle methods for role sections
const toggleRoleSection = (role: string) => {
  expandedRoleSections.value[role] = !expandedRoleSections.value[role]
}

const toggleAssignedRoleSection = (role: string) => {
  expandedAssignedRoleSections.value[role] = !expandedAssignedRoleSections.value[role]
}

// Load config on mount
onMounted(() => {
  // Don't auto-load - wait for user action
})
</script>
